import { ReactNode, useEffect } from 'react';
import {
  AgxEmptyState,
  AgxRow,
  AgxSaveChangesButton,
  SAVE_BUTTON_STATES,
  useToast,
} from '@urbanx/agx.ui-components2';
import { FormPrompt } from 'components/FormPrompt';
import LoadingState from 'components/loadingState/LoadingState';
import { MenuTitles, Pages } from 'constants/menu';
import { ErrorModal } from 'helpers/ErrorModal';
import { useSelectedCampaign } from 'hooks';
import { ContractPageScaffold } from '../ContractPageScaffold';
import TerminateContractAction from '../TerminateContractAction';
import { getContractInformationSubPages } from './ContractInformationSubPages';
import { ContractInformationEditor } from './useContractInformationEditor';

const saveToastId = 'contract-information-save';

interface ContractInformationPageProps<TDraft, TConfirmation> {
  currentPage: Pages;
  editor: ContractInformationEditor<TDraft, TConfirmation>;
  children: ReactNode;
}

export const ContractInformationPage = <TDraft, TConfirmation>({
  currentPage,
  editor,
  children,
}: ContractInformationPageProps<TDraft, TConfirmation>) => {
  const { campaign } = useSelectedCampaign();
  const { pushToast } = useToast();

  useEffect(() => {
    if (!editor.feedback) return;

    pushToast(
      {
        message: editor.feedback.message,
        type: editor.feedback.type,
      },
      saveToastId
    );
  }, [editor.feedback, pushToast]);

  if (editor.isLoading) return <LoadingState />;

  const saveState = (() => {
    if (editor.saving) return SAVE_BUTTON_STATES.SAVING;
    if (editor.dirty) return SAVE_BUTTON_STATES.UNSAVED;
    if (editor.feedback) return SAVE_BUTTON_STATES.SAVED;
    if (editor.loadFailed) return SAVE_BUTTON_STATES.ERROR;
    return SAVE_BUTTON_STATES.UNSAVED;
  })();

  return (
    <ContractPageScaffold
      pageTitle="Contract Information"
      secondaryActiveMenu={MenuTitles.CONTRACT_INFORMATION}
      currentPage={currentPage}
      subPages={getContractInformationSubPages({ campaignState: campaign?.state })}
      secondaryActions={<TerminateContractAction />}
      beforeContent={<FormPrompt hasUnsavedChanges={editor.dirty} />}
      headerAction={
        <AgxRow gap="spacing-md" fill alignItems="alignCenter" justifyContent="right">
          <AgxSaveChangesButton
            state={saveState}
            id="btnSaveChanges"
            text="Save Changes"
            size="small"
            variant="primary"
            onClick={() => void editor.save()}
            disabled={!editor.dirty || editor.saving}
          />
        </AgxRow>
      }
    >
      {editor.loadFailed ? (
        <AgxEmptyState variant="minimal" label="Unable to load contract information." />
      ) : (
        children
      )}
      {editor.errors.length > 0 && (
        <ErrorModal
          errorList={[...editor.errors]}
          setShowModal={(showModal) => {
            if (!showModal) editor.dismissErrors();
          }}
        />
      )}
    </ContractPageScaffold>
  );
};
